import React, { useCallback, useMemo, useRef } from "react";
import { ArrayField, useFieldArray, useWatch } from "react-hook-form";
import { TemplateQuestion } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import { FormStepProps } from "../../screens/MadFormScreen";
import {
  AnalyticsIcon,
  DeleteIcon,
  MenuIcon as TextLongIcon,
  PlusIcon,
  SelectorMultipleIcon,
  SelectorSingleIcon,
  StarIcon,
  TextShortIcon,
} from "../icons";
import { Button, Checkbox, Input, Tooltip } from "../wombat-ui";
import TemplateQuestionFormFooter from "./TemplateQuestionFormFooter";
import TemplateQuestionFormHeader from "./TemplateQuestionFormHeader";
import TemplateQuestionPreview from "./TemplateQuestionPreview";

export const SkillGoalOption: React.FC<
  Omit<
    FormStepProps,
    "onNext" | "formState" | "getValues" | "unregister" | "reset" | "errors"
  > & {
    fieldIndex: number;
    defaultValue: boolean;
    name: string;
  }
> = React.memo(
  ({ fieldIndex, register, control, setValue, defaultValue, name }) => {
    const t = useTranslations();
    const options: any = useWatch({
      control,
      name: `${name}.options`,
    });

    const handleOptionChecked = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked)
          options.forEach((o, innerIdx) => {
            if (o.isGoal && innerIdx !== fieldIndex)
              setValue(`${name}.options[${innerIdx}].isGoal`, false);
          });
        setValue(`${name}.options[${fieldIndex}].isGoal`, e.target.checked);
      },
      [options, fieldIndex, name]
    );

    return (
      <Checkbox
        label="Set as a goal"
        srOnlyLabel
        checkedIcon={
          <Tooltip label={t("mads.unsetAsGoal") as string}>
            <StarIcon
              className="cursor-pointer"
              iconClassName="text-warning-500 fill-current w-5 h-5"
            />
          </Tooltip>
        }
        uncheckedIcon={
          <Tooltip label={t("mads.setAsGoal") as string}>
            <StarIcon
              className="cursor-pointer"
              iconClassName="text-warning-300 w-5 h-5"
            />
          </Tooltip>
        }
        id={`${name}.options[${fieldIndex}].isGoal`}
        name={`${name}.options[${fieldIndex}].isGoal`}
        defaultChecked={defaultValue}
        ref={register()}
        onChange={handleOptionChecked}
      />
    );
  }
);

export type TemplateQuestionFormInput = Omit<
  TemplateQuestion,
  "id" | "defaultValue" | "optionsSchema" | "defaultValueSchema" | "__typename"
>;

export const TemplateQuestionWithOptionsForm: React.FC<
  Omit<FormStepProps, "onNext" | "formState" | "reset"> & {
    defaultValue: Partial<ArrayField<TemplateQuestionFormInput, "id">>;
    fixedOptions?: boolean;
    hidden?: boolean;
    name: string;
  }
> = React.memo(
  ({
    control,
    register,
    setValue,
    fixedOptions = false,
    hidden = false,
    name,
  }) => {
    const t = useTranslations();
    const { append, remove, fields } = useFieldArray<{
      value: string;
      label: string;
    }>({
      control,
      name: `${name}.options`,
    });
    const inputRef = useRef<HTMLInputElement>();

    return (
      <div className="grid grid-cols-12 gap-2 pt-2">
        {fields.map((field, fieldIndex) => {
          return (
            <React.Fragment key={field.id}>
              <div
                className="flex items-center w-full col-span-8 col-start-2 space-x-2"
                key={field.id}
              >
                <span className="w-20 text-xs text-primary-800">
                  Option {fieldIndex + 1}
                </span>
                <div className="flex-grow">
                  <Input
                    srOnlyLabel
                    label={`Option ${fieldIndex}`}
                    name={`${name}.options[${fieldIndex}].label`}
                    defaultValue={field.label}
                    ref={register()}
                    readOnly={fixedOptions}
                    id={`${name}.options[${fieldIndex}].label`}
                  />
                  <input
                    type="hidden"
                    ref={register()}
                    defaultValue={`${fieldIndex}`}
                    name={`${name}.options[${fieldIndex}].value`}
                  />
                </div>
              </div>
              {!fixedOptions && (
                <Button
                  icon={DeleteIcon}
                  variant="ghost"
                  onClick={() => {
                    remove(fieldIndex);
                  }}
                />
              )}
              {fixedOptions && (
                <div className="flex items-center col-span-1 px-2">
                  <SkillGoalOption
                    register={register}
                    setValue={setValue}
                    control={control}
                    defaultValue={(field as any)?.isGoal}
                    name={name}
                    fieldIndex={fieldIndex}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
        {!fixedOptions && (
          <React.Fragment>
            <div className="flex items-center w-full col-span-8 col-start-2 space-x-2">
              <div className="flex-grow">
                <Input
                  srOnlyLabel
                  label={t("mads.addOption") as string}
                  placeholder={t("mads.addOption") as string}
                  id={`${name}.addOptionLabel`}
                  ref={inputRef}
                />
              </div>
            </div>
            <div className="flex-col items-center justify-center mt-0.5">
              <Button
                icon={PlusIcon}
                id={`${name}.addOption`}
                onClick={() => {
                  if (inputRef?.current?.value) {
                    append({
                      value: fields.length.toString(),
                      label: inputRef.current.value,
                    });
                    inputRef.current.value = "";
                  }
                }}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
);

const TemplateQuestionForm: React.FC<
  Omit<FormStepProps, "onNext" | "formState"> & {
    defaultValue: Partial<ArrayField<TemplateQuestionFormInput, "id">>;
    shouldSelectReferent: boolean;
    index: number;
    onRemove: (index: number) => void;
    focused: boolean;
    inline?: boolean;
    name: string;
  }
> = React.memo(
  ({
    register,
    unregister,
    setValue,
    getValues,
    reset,
    shouldSelectReferent,
    index,
    control,
    errors,
    defaultValue,
    onRemove,
    focused,
    inline = false,
    name,
  }) => {
    const icon = useMemo(() => {
      const className = "w-5 h-5 text-primary-500";
      switch (defaultValue.type) {
        case "TemplateQuestionTextShort":
          return <TextShortIcon iconClassName={className} />;
        case "TemplateQuestionTextLong":
          return <TextLongIcon iconClassName={className} />;
        case "TemplateQuestionSelectorSingle":
          return <SelectorSingleIcon iconClassName={className} />;
        case "TemplateQuestionSelectorMultiple":
          return <SelectorMultipleIcon iconClassName={className} />;
        case "TemplateQuestionSkill":
          return <AnalyticsIcon iconClassName={className} />;
        case "TemplateQuestionEvaluation":
          return <StarIcon iconClassName={className} />;
        default:
          return null;
      }
    }, [defaultValue.type]);

    const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);

    return (
      <>
        <TemplateQuestionPreview
          control={control}
          defaultValue={defaultValue}
          errors={errors}
          name={name}
          icon={icon}
          focused={focused}
        />
        <div
          className={`px-4 py-3 bg-white
            ${!inline && "rounded shadow-md"}
            ${focused ? !inline && "border-l-8 border-secondary-500" : "hidden"}
          `}
        >
          <input
            type="hidden"
            name={`${name}.type`}
            ref={register()}
            defaultValue={defaultValue.type}
          />
          <TemplateQuestionFormHeader
            register={register}
            unregister={unregister}
            setValue={setValue}
            getValues={getValues}
            defaultValue={defaultValue}
            icon={icon}
            errors={errors}
            name={name}
          />
          {!["TemplateQuestionTextShort", "TemplateQuestionTextLong"].includes(
            defaultValue.type
          ) && (
            <TemplateQuestionWithOptionsForm
              control={control}
              register={register}
              getValues={getValues}
              errors={errors}
              unregister={unregister}
              setValue={setValue}
              defaultValue={defaultValue}
              fixedOptions={
                defaultValue.type === "TemplateQuestionEvaluation" ||
                defaultValue.type === "TemplateQuestionSkill"
              }
              name={name}
            />
          )}
          <TemplateQuestionFormFooter
            register={register}
            unregister={unregister}
            setValue={setValue}
            reset={reset}
            shouldSelectReferent={shouldSelectReferent}
            getValues={getValues}
            control={control}
            reportTypes={defaultValue?.reportTypes || []}
            referentIds={defaultValue?.referentIds || []}
            timeTracked={defaultValue?.timeTracked}
            expectedTimeValue={defaultValue?.expectedTimeValue}
            onRemove={handleRemove}
            name={name}
          />
        </div>
      </>
    );
  }
);

export default TemplateQuestionForm;
